import { IDictionary } from "@/shared/models/interface/dictionary-model";

class QueryStringService {
    public setQueryString(searchQuery: string, scopes: string[], filters: IDictionary<string[]>): void {
        let queryString: string[] = [];
        
        if (searchQuery) {
            queryString.push(`search=${searchQuery}`);
        }

        if (scopes && scopes.length) {
            queryString.push(`scope=${scopes.join('|')}`);
        }

        if (Object.keys(filters)?.length) {
            let filterQueryString: string[] = Object.keys(filters).map((x: string) => {
                // Normally spaces are encoded as %20. However, we want to use
                // in order to keep the URL structure consistent.
                let encodedValues = filters[x].map((value: string) => encodeURIComponent(value).replaceAll('%20', '+'));
                return `${x}:${encodedValues.join(',')}`;
            });

            queryString.push(`filters=${filterQueryString.join('|')}`);
        }

        if (queryString.length) {
            // Spaces in a query string should be encoded as a plus sign.
            let query = queryString.join('&').replaceAll(' ', '+');

            // Only update the query string if it has changed, so we don't
            // create a new history entry. We encode the query string to prevent
            // query strings with special characters to be considered different.
            if (
                encodeURIComponent(window.location.search.replaceAll(' ', '+')) !=
                encodeURIComponent(`?${query}`)
            ) {
                window.history.pushState(null, document.title, `?${query}`);
            }
        }
    }

    public getSearch(): string | null {
        const searchRegex: RegExp = /search=([^&]+)/;
        return this.getItem(searchRegex);
    }

    public getScopes(): string[] | null {
        const scopesRegex: RegExp = /scope=([^&]+)/;
        let scopes = this.getItem(scopesRegex);
        return scopes ? scopes.split("|") : null;
    }

    public getFilters(): string[] | null {
        const filtersRegex: RegExp = /filters=([^]+)/;
        let filterString = this.getItem(filtersRegex);
        let filterItems: string[] = [];

        filterString?.split("|").map((filter: string) => {
            let [filterGroup, filterNames] = filter.split(":");
            filterItems.push(...filterNames.split(/,(?!\s)/));
        });

        return filterItems ?? null;
    }

    private getItem(regex: RegExp): string | null {
        let queryString = decodeURIComponent(document.location.search);

        if (!queryString) {
            // We used to store everything in the url fragment instead of the
            // query string, so in order to be backwards compatible with
            // potential hardcoded links or saved favourite to searches we also
            // check the url fragment when we don't have a query string.
            let hash = decodeURIComponent(document.location.hash);
            if (!hash) {
                return null
            }

            let matches = hash.match(regex);        
            return matches ? decodeURI(matches[1].replaceAll('+', ' ')) : null;
        }

        let matches = queryString.match(regex);
        return matches ? decodeURI(matches[1].replaceAll('+', ' ')) : null;
    }
}

export default new QueryStringService();
